import { FieldArray, FormikProps } from 'formik';
import { FaImage } from 'react-icons/fa';
import Cleave from 'cleave.js/react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from 'reactstrap';
import {
  Composite,
  GetProductByIdFromAPI,
  ProductFormDetailsInterface,
} from 'types/productTypes';
import { handleSearchChange } from 'pages/Products/ProductManagement/FormHandlers';
import { compositeLimit } from 'pages/Products/ProductConstants';
import { useSelector } from 'react-redux';
import styles from 'pages/Products/styles/products.module.css';
import { useTranslation } from 'react-i18next';
import { Dispatch } from 'redux';
import { RootState } from 'slices';
import { DISPLAY_COMPOSITE_IMAGE_SIZE } from 'pages/Products/ProductConstants';
import { ImageWithFallback } from 'Components/pos2u/ImageWithFallback';
import { RiDeleteBin2Line } from 'react-icons/ri';

const ProductComposite = ({
  dispatch,
  formik,
  toggleModal,
  searchInputRef,
  selectProduct,
  closeModal,
}: {
  dispatch: Dispatch;
  formik: FormikProps<ProductFormDetailsInterface>;
  toggleModal: (index?: number | null) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  selectProduct: (
    product: GetProductByIdFromAPI,
    formik: FormikProps<ProductFormDetailsInterface>
  ) => void;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const productFormState = useSelector((state: RootState) => state.Product);
  const { isNoStock, isNoFragment, compositeModal, showAllValidateErrors } =
    productFormState.form;
  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded } = infoState;

  return (
    <FieldArray
      name="composites"
      render={(arrayHelpers) => (
        <Row
          className={`stock-group ${isNoStock ? 'd-none' : 'stock-hide'} ${
            isNoFragment ? 'd-none' : 'fragment-hide'
          }`}
        >
          <Col xs={0} sm={3} className="mb-2 mb-xs-0"></Col>
          <Col xs={12} sm={9} className="mb-2 mb-xs-0">
            <Card className={`${styles['border-dashed']}`}>
              <CardBody className={`${styles['card-body-pd']}`}>
                <Row className="align-items-center mb-3 mb-xs-0 justify-content-center">
                  <Col xs={6} sm={7} className="mb-2 mb-xs-0">
                    <Label className={`${styles['text-pd']}`}>
                      {t('ProductField_ItemName')}
                    </Label>
                  </Col>
                  <Col xs={6} sm={5} className="mb-2 mb-xs-0">
                    <Label>{t('quantity')}</Label>
                  </Col>
                </Row>

                {formik.values.composites.map(
                  (product: Composite, index: number) => (
                    <Row
                      key={index}
                      className="align-items-top mb-3 mb-xs-0 px-0 mx-0"
                    >
                      <Col xs={6} sm={7} className="mb-2 mb-xs-0">
                        <div className="input-container">
                          <Input
                            type="text"
                            name={`composites[${index}].name`}
                            value={formik.values.composites[index].name}
                            placeholder={t('ProductField_ItemName')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            onClick={() => toggleModal(index)}
                            className={`form-control`}
                            readOnly
                          />
                          <input
                            type="hidden"
                            name={`composites[${index}].item_id_number`}
                            defaultValue={
                              formik.values.composites[index].item_id_number
                            }
                          />

                          {(formik.touched.composites?.[index]?.name &&
                            formik.errors.composites?.[index] &&
                            typeof formik.errors.composites[index] ===
                              'object' &&
                            'name' in formik.errors.composites[index] &&
                            typeof formik.errors.composites[index].name ===
                              'string') ||
                          (formik.isValid === false &&
                            formik.errors.composites?.[index] &&
                            typeof formik.errors.composites[index] ===
                              'object' &&
                            'name' in formik.errors.composites[index] &&
                            typeof formik.errors.composites[index].name ===
                              'string' &&
                            showAllValidateErrors === true) ? (
                            <div className="text-danger">
                              {formik.errors.composites[index].name}
                            </div>
                          ) : null}

                          <Modal
                            isOpen={compositeModal.isModalOpen}
                            toggle={closeModal}
                            centered
                          >
                            <ModalHeader
                              className="bg-light p-3"
                              toggle={closeModal}
                            >
                              🔎 {t('ProductPage_SearchItem')}
                            </ModalHeader>
                            <ModalBody>
                              <Input
                                type="text"
                                value={compositeModal.searchTerm}
                                onChange={(e) => {
                                  handleSearchChange(
                                    e,
                                    merchant,
                                    isMerchantLoaded,
                                    dispatch
                                  );
                                }}
                                innerRef={searchInputRef}
                                placeholder={
                                  t('ProductPage_SearchItem') + '...'
                                }
                              />
                              <ul
                                style={{
                                  listStyleType: 'none',
                                  paddingLeft: 0,
                                }}
                              >
                                {compositeModal.searchResults.map(
                                  (
                                    result: GetProductByIdFromAPI,
                                    idx: number
                                  ) => (
                                    <li
                                      key={idx}
                                      style={{
                                        padding: '10px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        transition:
                                          'background-color 0.3s ease, box-shadow 0.3s ease',
                                        cursor: 'pointer',
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                          '#f0f0f0')
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                          '')
                                      }
                                      onClick={() =>
                                        selectProduct(result, formik)
                                      }
                                    >
                                      <div className={`image-container`}>
                                        <div
                                          className={`${styles['zoom']}`}
                                          style={{
                                            width: DISPLAY_COMPOSITE_IMAGE_SIZE,
                                            height:
                                              DISPLAY_COMPOSITE_IMAGE_SIZE,
                                            overflow: 'hidden',
                                            display: 'inline-block',
                                            marginRight: '10px',
                                            transition: 'transform 0.3s ease',
                                          }}
                                        >
                                          {result.images?.thumbs &&
                                          result.images?.thumbs !== '' ? (
                                            <a
                                              href={result.images?.large}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                textDecoration: 'none',
                                              }}
                                            >
                                              <ImageWithFallback
                                                src={result.images?.thumbs}
                                                alt={result.name}
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover',
                                                  borderRadius: '0.25rem',
                                                }}
                                                errImgSize={DISPLAY_COMPOSITE_IMAGE_SIZE.toString()}
                                              />
                                            </a>
                                          ) : (
                                            <FaImage
                                              style={{
                                                fontSize:
                                                  DISPLAY_COMPOSITE_IMAGE_SIZE,
                                              }}
                                              color="gray"
                                            />
                                          )}
                                        </div>
                                      </div>

                                      {result.name}
                                    </li>
                                  )
                                )}
                              </ul>
                            </ModalBody>
                          </Modal>
                        </div>
                      </Col>
                      <Col xs={4} sm={3} className="mb-2 mb-xs-0">
                        <div className="input-container">
                          {/* <Input
                            type="number"
                            name={`composites[${index}].quantity`}
                            value={formik.values.composites[index].quantity}
                            placeholder={t('quantity')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`form-control ${styles['no-px']}`}
                          /> */}

                          <Cleave
                            // className="form-control"
                            className={`form-control ${styles['no-px']}`}
                            name={`composites[${index}].quantity`}
                            options={{
                              numeral: true,
                              numeralDecimalScale: 3,
                              numeralIntegerScale: 13,
                              numeralThousandsGroupStyle: 'thousand',
                            }}
                            value={formik.values.composites[index].quantity}
                            placeholder={t('quantity')}
                            onChange={(e: any) => {
                              formik.setFieldValue(
                                `composites[${index}].quantity`,
                                e.target.rawValue
                              );
                            }}
                            onBlur={formik.handleBlur}
                          />

                          {(formik.touched.composites?.[index]?.quantity &&
                            formik.errors.composites?.[index] &&
                            typeof formik.errors.composites[index] ===
                              'object' &&
                            'quantity' in formik.errors.composites[index] &&
                            typeof formik.errors.composites[index].quantity ===
                              'string') ||
                          (formik.isValid === false &&
                            formik.errors.composites?.[index] &&
                            typeof formik.errors.composites[index] ===
                              'object' &&
                            'quantity' in formik.errors.composites[index] &&
                            typeof formik.errors.composites[index].quantity ===
                              'string' &&
                            showAllValidateErrors === true) ? (
                            <div className="text-danger">
                              {formik.errors.composites[index].quantity}
                            </div>
                          ) : null}
                        </div>
                      </Col>
                      <Col
                        xs={2}
                        sm={2}
                        className="d-flex align-items-center mb-2 mb-xs-0"
                        style={{
                          padding: '0.5rem 0.9rem',
                        }}
                      >
                        {formik.values.composites.length > 0 && (
                          <div
                            onClick={() => arrayHelpers.remove(index)}
                            role="button"
                            tabIndex={0}
                            style={{
                              cursor: 'pointer',
                            }}
                            className="d-flex align-items-center justify-content-center"
                          >
                            <RiDeleteBin2Line color="red" size="20px" />
                          </div>
                        )}
                      </Col>
                    </Row>
                  )
                )}

                {formik.errors.composites &&
                  typeof formik.errors.composites === 'string' && (
                    <div className="text-danger mb-1">
                      {formik.errors.composites}
                    </div>
                  )}

                <Row>
                  <Col sm={12} xs={12}>
                    <div className={`${styles['text-pd']}`}>
                      <Button
                        className="btn me-2"
                        type="button"
                        color="success"
                        onClick={() =>
                          formik.values.composites.length < compositeLimit &&
                          arrayHelpers.push({
                            name: '',
                            item_id_number: '',
                            quantity: 1,
                          })
                        }
                        disabled={
                          formik.values.composites.length >= compositeLimit
                        }
                      >
                        + {t('Btn_Add')}
                      </Button>
                      {formik.values.composites.length >= compositeLimit && (
                        <div className="text-danger mt-2">
                          {t('ProductPage_Warn_Must_not_exceed')}{' '}
                          {compositeLimit} {t('ProductPage_Warn_items')}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    />
  );
};
export default ProductComposite;
